import { Callout } from '@workspace/ui/components/callout'
import { CheckRow } from '@workspace/ui/components/check-row'
import { StatusRow } from '@workspace/ui/components/status-row'
import { toneBorder, toneText } from '@workspace/ui/lib/tone'
import { cn } from '@workspace/ui/lib/utils'
import type { DiagnosticVisualProps } from './types'

/**
 * The body of an alert card: what state the campaign is in, the number that
 * makes it concrete, and what stopped it.
 *
 * Every part is optional because no two of the five alert cards are built the
 * same way — the stall leads with a callout and has no status row, add-funds
 * has a status row and a big number, the read failure has neither.
 *
 * The row order carries the argument: the one thing that is wrong, then the
 * things that are fine. That is what makes an alert readable instead of
 * alarming — the founder can see the blast radius is small.
 */
export function DiagnosticVisual({
  tone = 'alert',
  header,
  bigNumber,
  rowsHeading,
  rows,
}: DiagnosticVisualProps) {
  return (
    <div className={cn('rounded-xl border bg-card/40 px-5 py-4', toneBorder(tone))}>
      {header?.kind === 'status' && (
        <StatusRow
          tone={header.tone}
          label={header.label}
          detail={header.detail}
          className="mb-3"
        />
      )}
      {/* Only a red card gets a red callout. Everything else — an ask, a
          neutral state — takes amber: the sentence is the loudest thing in the
          panel, and painting "nobody approved yet" the same red as a broken
          sender says something went wrong when nothing did. */}
      {header?.kind === 'callout' && (
        <Callout tone={tone === 'alert' ? 'bad' : 'warn'} className="mb-3.5">
          {header.text}
        </Callout>
      )}

      {bigNumber && (
        <div className="mb-1 flex items-baseline gap-2.5">
          <span
            className={cn(
              'text-[34px] font-bold leading-none tracking-[-0.03em] tabular-nums',
              toneText(tone)
            )}
          >
            {bigNumber.value}
          </span>
          <span className="text-[13.5px] text-muted-foreground">{bigNumber.sentence}</span>
        </div>
      )}

      {rows.length > 0 && (
        <div className={bigNumber || header ? 'mt-3.5 border-t border-border/40 pt-3' : undefined}>
          {rowsHeading && (
            <div className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.13em] text-muted-foreground">
              {rowsHeading}
            </div>
          )}
          {rows.map((row) => (
            <CheckRow key={row.text} tone={row.tone}>
              {row.text}
            </CheckRow>
          ))}
        </div>
      )}
    </div>
  )
}
